import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Facebook, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG, SITE } from "@/lib/site";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-13 rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Contact | TRX Auto Sales & Service Inc — Statesville NC";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        lines={["Ready To Upgrade Your", "Automotive Experience?"]}
        sub="Call, text, email, or stop by the shop — we answer like enthusiasts, not a call center."
        image={IMG.road}
      />

      <section className="py-16 md:py-24" data-testid="contact-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <div className="space-y-px border border-white/10 bg-white/10">
                <a href={SITE.phoneHref} data-testid="contact-phone" className="group flex items-center gap-6 bg-trx-black p-7 transition-colors duration-300 hover:bg-card">
                  <span className="border border-white/15 p-3.5 text-trx-blue transition-colors duration-300 group-hover:border-trx-blue"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Call or Text</p>
                    <p className="mt-1 font-display text-xl font-bold text-white">{SITE.phone}</p>
                  </div>
                </a>
                <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="group flex items-center gap-6 bg-trx-black p-7 transition-colors duration-300 hover:bg-card">
                  <span className="border border-white/15 p-3.5 text-trx-blue transition-colors duration-300 group-hover:border-trx-blue"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Email</p>
                    <p className="mt-1 font-display text-xl font-bold text-white">{SITE.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-6 bg-trx-black p-7">
                  <span className="border border-white/15 p-3.5 text-trx-blue"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Visit</p>
                    <p className="mt-1 font-display text-xl font-bold text-white">{SITE.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-6 bg-trx-black p-7">
                  <span className="border border-white/15 p-3.5 text-trx-blue"><Clock className="h-5 w-5" /></span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Hours</p>
                    {SITE.hours.map((h) => (
                      <p key={h.day} className="mt-1 text-sm text-zinc-300">
                        <span className="text-zinc-500">{h.day}:</span> {h.time}
                      </p>
                    ))}
                  </div>
                </div>
                <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="group flex items-center gap-6 bg-trx-black p-7 transition-colors duration-300 hover:bg-card">
                  <span className="border border-white/15 p-3.5 text-trx-blue transition-colors duration-300 group-hover:border-trx-blue"><Facebook className="h-5 w-5" /></span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Social</p>
                    <p className="mt-1 font-display text-xl font-bold text-white">Follow us on Facebook</p>
                  </div>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              {done ? (
                <div className="flex h-full flex-col items-center justify-center border border-trx-blue/40 bg-card p-10 text-center" data-testid="contact-confirmation">
                  <CheckCircle2 className="h-12 w-12 text-trx-blue" />
                  <h2 className="mt-6 font-display text-3xl font-black tracking-tight text-white">Message Sent.</h2>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">
                    Thanks for reaching out. We'll get back to you within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-white/10 bg-card p-8 md:p-10" data-testid="contact-form">
                  <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">Send Us A Message</h2>
                  <div className="mt-8 grid gap-5">
                    <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                    <div className="grid gap-5 md:grid-cols-2">
                      <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                      <Input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                    </div>
                    <Textarea
                      required
                      placeholder="How can we help? *"
                      value={form.message}
                      onChange={set("message")}
                      className="min-h-[150px] rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
                      data-testid="contact-message"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="group flex items-center justify-center gap-3 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50"
                    >
                      {submitting ? "Sending…" : "Send Message"}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10" data-testid="contact-map-section">
        <div className="relative">
          <iframe
            title="TRX Auto Sales & Service Inc location"
            src={SITE.mapsEmbed}
            className="h-[420px] w-full grayscale invert-[0.92] contrast-[0.9]"
            loading="lazy"
            data-testid="contact-map"
          />
          <div className="pointer-events-none absolute inset-0 border-b border-white/10" />
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="contact-cta">
        <div className="mx-auto max-w-[1600px] px-6 text-center md:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tighter text-white">
              Prefer to talk it through? Call us — (704) 380-4555.
            </h2>
            <Link to="/service" data-testid="contact-schedule-link" className="mt-8 inline-block text-xs font-bold uppercase tracking-[0.25em] text-trx-blue link-underline">
              Or schedule service online
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
